import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Spinner from 'components/loader/Spinner';
import { BackButton } from 'components/buttons';
import Header, { ButtonConfig } from 'components/header/Header';
import EditModal from './components/EditModal';
import useOrganization from './hooks/useOrganization';
import EditorDetails from 'components/details/EditorDetails';
import { NButton, NTable } from '@innersource-nn/novoui-react';
import Pagination from 'components/pager/Pagination';
import useEnvironmentList, {
    OrganizationEnvironments,
} from './hooks/useEnvironmentList';
import { SearchFilter } from 'types/Filters';
import TableActionButton from 'components/buttons/TableActionButton';
import useArchiveOrganization from './hooks/useArchiveOrganization';
import InboxIcon from 'assets/icons/InboxIcon';
import DeleteModal from 'components/modal/DeleteModal';

const tableHeaders = [
    { header: 'Name', key: 'label' },
    { header: 'Environment URI', key: 'environmentUri' },
    { header: 'AWS Accounts', key: 'accounts' },
    { header: 'Admin groups', key: 'SamlGroupName' },

    { header: 'Created By', key: 'owner' },
    { header: '', key: 'action' },
];
const OrganizationView = () => {
    const navigate = useNavigate();
    const params = useParams();
    const {
        organization,
        disableDelete,
        userIsAdmin,
        items,
        error,
        loading,
        fetchData,
    } = useOrganization();
    const { archiveOrg, deleteLoading } = useArchiveOrganization();
    const [filter, setFilter] = useState<SearchFilter>({
        page: 1,
        pageSize: 10,
        term: '',
        onlyProduction: true,
    });
    const { environments, tableItems } = useEnvironmentList(filter);
    const organizationUri = params.uri;
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);

    const getAccounts = (environment: OrganizationEnvironments) => {
        const accounts = [
            { label: 'PROD', id: environment.AwsProdAccountId },
            { label: 'VAL', id: environment.AwsValAccountId },
            { label: 'TEST', id: environment.AwsTestAccountId },
            { label: 'TOOL', id: environment.AwsToolAccountId },
            { label: 'DEV', id: environment.AwsDevAccountId },
        ]
            .filter((account) => account.id)
            .map((account, index) => (
                <div className="mt-8 mb-8" key={index}>
                    {`${account.label} - ${account.id}`}
                </div>
            ));

        return accounts;
    };

    const adjustItems = (items: OrganizationEnvironments[]) => {
        return items.map((item) => {
            return {
                ...item,
                accounts: getAccounts(item),
                owner: item.owner.split('@')[0].toUpperCase(),
                action: (
                    <TableActionButton
                        path={`/environment/${item.environmentUri}`}
                    />
                ),
            };
        });
    };
    const disableTooltip = !userIsAdmin
        ? 'To access this functionality you must be Owner or Admin of this Organization'
        : '';

    const buttonConfig: ButtonConfig[] = [
        {
            label: 'Edit',
            onClick: () => setEditModalOpen(true),
            icon: 'edit',
            disabled: !userIsAdmin,
            tooltip: disableTooltip,
        },
        {
            label: 'Delete',
            onClick: () => setDeleteModalOpen(true),
            red: true,
            disabled: !userIsAdmin || disableDelete,
            icon: 'trash',
            tooltip:
                disableTooltip ||
                (disableDelete
                    ? 'You need to delete the linked environments first'
                    : ''),
        },
    ];

    return (
        <div>
            <EditModal
                visible={editModalOpen}
                onClose={() => {
                    fetchData();
                    setEditModalOpen(false);
                }}
            />
            <DeleteModal
                title="Delete organization"
                type="organization"
                onClick={archiveOrg}
                loading={deleteLoading}
                visible={deleteModalOpen}
                onClose={() => setDeleteModalOpen(false)}
            />
            {loading ? (
                <Spinner />
            ) : (
                <div>
                    <Header
                        title={organization.label}
                        buttonConfigs={buttonConfig}
                        backButton
                    />
                    <div className="text-true-blue text-sm">
                        Created: {organization.created}
                    </div>
                    <div className="info-container">
                        <h3 className="text-2xl font-bold mb-16">
                            Organization details
                        </h3>
                        <EditorDetails
                            className="grid-cols-2"
                            loading={loading}
                            error={error}
                            items={items}
                            type="organization"
                        />
                    </div>
                    <div className="info-container">
                        <div className="flex justify-between items-center mb-16">
                            <h3 className="text-2xl font-bold">
                                Environment List
                            </h3>
                            <NButton
                                square
                                variant="outline"
                                size="small"
                                leftIcon="circleAdd"
                                onClick={() =>
                                    navigate(
                                        `/new-environment/${organizationUri}`,
                                    )
                                }
                            >
                                Link new environment
                            </NButton>
                        </div>
                        {tableItems.count === 0 ? (
                            <div className="border border-faded-blue-300 rounded-8 py-32">
                                <div className="items-center">
                                    <div className="flex flex-col items-center">
                                        <div className="round-div-icon-middle bg-light-blue-200 h-[5rem] w-[5rem] ">
                                            <InboxIcon className="w-40 h-40" />
                                        </div>
                                        <p className="text-true-blue w-1/2 text-center mt-8">
                                            It looks like you dont have any
                                            environment linked to this
                                            organization yet.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div>
                                <NTable
                                    headers={tableHeaders}
                                    columnBorder={true}
                                    items={adjustItems(environments)}
                                    rowKey="environmentUri"
                                />
                                {!loading && tableItems.count > 0 && (
                                    <Pagination
                                        items={tableItems}
                                        filter={filter}
                                        setFilter={setFilter}
                                    />
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default OrganizationView;
